import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { type Timeframe } from './useStudentGenerateMessageMutation';
import { timeframeToApiParams } from './studentDataParams';

const log = createLogger('useStudentBehaviorReportQuery');

export const STUDENT_BEHAVIOR_REPORT_QUERY_KEY = 'studentBehaviorReport';

export type BehaviorSeries = {
  key: string;
  label: string;
  data: (number | null)[];
};

export type BehaviorReportChartData = {
  ticks: string[];
  tickDisplayLabels: string[];
  series: BehaviorSeries[];
};

type BehaviorReportApiResponse = {
  ticks: string[];
  series: { key: string; label: string; data: (number | null)[] }[];
  interval: string;
};

/** "student-abc123_praises" → "praises" */
function normalizeKey(key: string): string {
  const idx = key.lastIndexOf('_');
  return idx >= 0 ? key.slice(idx + 1) : key;
}

/** "2026-04-02" → "Apr 2", "2026-03-16" → "Mar 16" */
function formatTickLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Reduces tick labels to at most 7 visible labels, aligned to the positions
 * LineChart will actually render (it skips every Math.floor(n/50)th tick
 * internally). Choosing indices outside that set produces empty Text elements.
 */
function thinTickLabels(labels: string[]): string[] {
  const n = labels.length;
  if (n <= 7) return labels;

  // Mirror LineChart's internal step filter so our labels land on rendered positions
  const chartStep = Math.max(1, Math.floor(n / 50));
  const renderable: number[] = [];
  for (let i = 0; i < n; i += chartStep) {
    renderable.push(i);
  }

  const count = Math.min(7, renderable.length);
  const shown = new Set<number>();
  for (let i = 0; i < count; i++) {
    const idx = count === 1 ? 0 : Math.round((i * (renderable.length - 1)) / (count - 1));
    const pos = renderable[idx];
    if (pos !== undefined) shown.add(pos);
  }

  return labels.map((label, i) => (shown.has(i) ? label : ''));
}

/** "Aaron (Praises)" → "Praises" */
function simplifyLabel(label: string): string {
  const match = label.match(/\(([^)]+)\)$/);
  return match?.[1] ?? label;
}

export function useStudentBehaviorReportQuery({
  studentId,
  periodId,
  periodCreatedAt,
  timeframe,
}: {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, status, isFetching } = useQuery<BehaviorReportChartData | null>({
    queryKey: [STUDENT_BEHAVIOR_REPORT_QUERY_KEY, studentId, periodId, timeframe, periodCreatedAt],
    queryFn: async () => {
      log.debug('Fetching student behavior report', { studentId, periodId, timeframe });

      const tz = locale?.timeZone ?? '';
      const dateParams = timeframeToApiParams(timeframe, periodCreatedAt);

      const params = new URLSearchParams({
        tz,
        studentIds: studentId,
        periodIds: periodId,
        indication: 'both',
        overlay: 'absence',
        interval: 'day',
      });
      if ('range' in dateParams && dateParams.range !== undefined) {
        params.set('range', dateParams.range);
      } else if ('start' in dateParams && dateParams.start !== undefined) {
        params.set('start', dateParams.start);
        params.set('end', dateParams.end!);
      }

      const result = await httpClient.get<BehaviorReportApiResponse>(`/report/getBehaviorReport?${params}`);

      const thinnedTicks = thinTickLabels(result.ticks.map(formatTickLabel));

      const chartData: BehaviorReportChartData = {
        ticks: thinnedTicks,
        tickDisplayLabels: result.ticks,
        series: result.series.map((s) => {
          const key = normalizeKey(s.key);
          const data = key === 'absent' ? s.data.map((v) => (v === 0 ? null : v)) : s.data;
          return { key, label: simplifyLabel(s.label), data };
        }),
      };

      log.info('Fetched student behavior report successfully', { studentId, tickCount: result.ticks.length });
      return chartData;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch student behavior report', failureCount, error, log }),
    enabled: !!studentId && !!periodId,
    refetchOnWindowFocus: false,
  });

  return { data, status, isFetching };
}
